import { Component } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { Portal } from 'src/models/portals';
import { TENANT_USER_ROLE_NAMES } from 'src/models/users';
import { AuthService } from 'src/services/auth.service';
import { PortalsService } from 'src/services/portals.service';

@Component({
  selector: 'app-profile-popover',
  templateUrl: './profile-popover.page.html',
  styleUrls: ['./profile-popover.page.scss'],
})
export class ProfilePopoverPage {
  name: string;
  email: string;
  roleName: string | null;
  portalLinks: Array<{ label: string; url: string }> = [];

  constructor(
    public popoverCtrl: PopoverController,
    public authService: AuthService,
    private portalsService: PortalsService,
  ) {
    const user = this.authService.user;
    this.name = this.authService.profile?.name ?? user.displayName;
    this.email = this.authService.profile?.email ?? user.email;

    const role = this.authService.profile?.role;
    this.roleName = role ? TENANT_USER_ROLE_NAMES[role] : null;

    void this.loadCustomerPortalUrl();
  }

  async sendFeedback() {
    (window as any).$unthread.inAppChat('toggle', 'show');
    await this.popoverCtrl.dismiss();
  }

  openHelpdesk() {
    window.open('https://docs.unthread.io/docs/introduction-to-unthread/introduction', '_blank');
    this.popoverCtrl.dismiss();
  }

  openSlackInbox() {
    const slackTeamUrl = this.authService.tenant?.slackTeamUrl;
    const slackBotId = this.authService.tenant?.slackBotId;
    const slackTeamId = this.authService.tenant?.slackTeamId;
    let url = '';

    if (slackTeamUrl) {
      url += `${slackTeamUrl}app_redirect?`;

      if (slackBotId) {
        url += `channel=${slackBotId}`;

        if (slackTeamId) {
          url += `&team=${slackTeamId}`;
        }
      } else {
        url += `id=${environment.slack.appID}`;
      }
    } else {
      url += `slack://`;

      if (slackBotId) {
        url += `channel?id=${slackBotId}`;

        if (slackTeamId) {
          url += `&team=${slackTeamId}`;
        }
      } else {
        url += `app?id=${environment.slack.appID}`;
      }
    }

    url += '&tab=home';

    window.open(url, '_blank');
    this.popoverCtrl.dismiss();
  }

  openPortal(url: string) {
    if (!url) {
      return;
    }

    window.open(url, '_blank');
    this.popoverCtrl.dismiss();
  }

  logout() {
    this.authService.logout();
    this.popoverCtrl.dismiss();
  }

  private async loadCustomerPortalUrl() {
    try {
      const response = await this.portalsService.list({ limit: 10 });
      const portals = response?.data ?? [];
      const internalPortal =
        portals.find((portal) => portal.isExternal === false && portal.isEnabled && this.buildPortalUrl(portal)) ??
        portals.find((portal) => portal.isExternal === false && this.buildPortalUrl(portal)) ??
        null;

      const internalPortalUrl = internalPortal ? this.buildPortalUrl(internalPortal) : null;
      const links: Array<{ label: string; url: string }> = [];

      if (internalPortalUrl) {
        links.push({ label: 'Internal support portal', url: internalPortalUrl });
      }

      this.portalLinks = links;
    } catch (error) {
      console.error('Failed to load customer portal link:', error);
    }
  }

  private buildPortalUrl(portal: Partial<Portal>): string | null {
    if (portal.customDomain) {
      return `https://${portal.customDomain}`;
    }

    if (portal.defaultDomain) {
      if (portal.defaultDomain.startsWith('http://') || portal.defaultDomain.startsWith('https://')) {
        return portal.defaultDomain;
      }

      return `https://${portal.defaultDomain}`;
    }

    if (portal.previewUrl) {
      return portal.previewUrl;
    }

    return null;
  }
}
